/**
 * Разбор написанного словами — на сервере (PRD §5.5, §18.3/Л2).
 *
 * Ключ модели в клиент не попадает ни в каком виде: браузер обращается к своему
 * /api, а к провайдеру ходит сервер. Поэтому же разбор требует входа — иначе
 * платный вызов доступен кому угодно.
 *
 * Модель извлекает поля и ничего больше: не создаёт операцию, не считает деньги
 * и не решает, хватает ли их. Разобранное лишь заполняет форму, а сохраняет
 * человек. Иначе появляется класс отказов «приложение записало не то, и я об
 * этом не узнал».
 */
import { pd, type ImportedReceipt, type PlanPart, type PlainDate } from '@skoka/core';
import { api, unwrap } from './sync.js';

export interface ParsedPhrase {
  type: 'expense' | 'income' | 'transfer' | 'unknown';
  /** Копейки целым числом либо null, если сумма в фразе не названа. */
  amount: number | null;
  category: string | null;
  categorySuggestion?: string | null;
  part: 'need' | 'want' | null;
  date: PlainDate | null;
  merchant: string | null;
  /** Счёт, названный во фразе («с Т-Банка»): у перевода — откуда. Не назван — null. */
  account: string | null;
  /** Куда перевели — только у перевода. */
  toAccount: string | null;
  /** 0..1. Ниже 0.5 интерфейс обязан спросить, а не молча заполнить. */
  confidence: number;
}

/** Настроен ли разбор на сервере. Без сессии, это состояние сервера. */
export async function aiConfigured(): Promise<boolean> {
  try {
    const body = await unwrap(await api('/ai/status'));
    return typeof body === 'object' && body !== null && (body as { configured?: unknown }).configured === true;
  } catch {
    // Офлайн или сервер недоступен — просто нет разбора, ручной ввод остаётся.
    return false;
  }
}

export async function parsePhrase(
  phrase: string, today: PlainDate, categories: import('./categories.js').Category[] = [],
  /** Счета, которые можно назвать во фразе: «кофе 300 с Т-Банка». */
  accounts: ReadonlyArray<{ id: string; name: string }> = [],
): Promise<ParsedPhrase> {
  const body = await unwrap(await api('/ai/parse-expense', {
    method: 'POST',
    body: JSON.stringify({
      phrase, today,
      categories: categories.filter(c => !c.archived).map(c => ({ id: c.id, name: c.name, kind: c.kind ?? 'expense' })),
      accounts: accounts.map(a => ({ id: a.id, name: a.name })),
    }),
  }));
  const raw = (body as { result?: unknown }).result;
  return normalize(raw);
}

/**
 * Приведение ответа к типу.
 *
 * Сервер уже проверяет ответ модели, но клиент не имеет права полагаться на
 * это: между ними сеть, версии и прокси. Всё сомнительное становится null —
 * пустое поле человек заметит и заполнит, а подставленное правдоподобное
 * значение подтвердит не глядя.
 */
function normalize(raw: unknown): ParsedPhrase {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const type = r.type === 'expense' || r.type === 'income' || r.type === 'transfer' ? r.type : 'unknown';
  const amount = typeof r.amount === 'number' && Number.isInteger(r.amount) && r.amount > 0 ? r.amount : null;
  const confidence = typeof r.confidence === 'number' && r.confidence >= 0 && r.confidence <= 1 ? r.confidence : 0;
  let date: PlainDate | null = null;
  if (typeof r.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.date)) {
    try { date = pd(r.date); } catch { date = null; }
  }
  return {
    type,
    amount,
    categorySuggestion: typeof r.categorySuggestion === 'string' ? r.categorySuggestion.trim().slice(0, 60) || null : null,
    category: typeof r.category === 'string' && r.category !== '' ? r.category : null,
    part: r.part === 'need' || r.part === 'want' ? r.part : null,
    date,
    merchant: typeof r.merchant === 'string' && r.merchant !== '' ? r.merchant : null,
    account: typeof r.account === 'string' && r.account !== '' ? r.account : null,
    toAccount: type === 'transfer' && typeof r.toAccount === 'string' && r.toAccount !== '' ? r.toAccount : null,
    confidence: amount === null ? Math.min(confidence, 0.4) : confidence,
  };
}

export interface ReceiptItem {
  name: string;
  qty: number;
  /** Копейки целым числом. */
  price: number;
  sum: number;
}

export interface ParsedReceipt {
  /** Состав можно показывать только при true: арифметика сошлась целиком. */
  trusted: boolean;
  /**
   * Что это за документ по мнению разбора.
   *
   * Человек загружает то, что у него есть, и это не всегда чек одной покупки.
   * Список операций из банковского приложения похож на чек ровно настолько,
   * чтобы сложить его строки и записать трату на сумму пяти разных покупок.
   */
  kind: ImportedReceipt['kind'];
  /** Подпись карты плательщика из документа; null, если её нет. */
  account?: string | null;
  total: number | null;
  /**
   * Итог посчитан по позициям, потому что в документе его нет.
   *
   * Показывается человеку рядом с числом: пропущенную строку в документе без
   * итога поймать нечем, и сумма может оказаться меньше настоящей.
   */
  totalFromItems: boolean;
  items: ReceiptItem[];
  /** Позиции с полной скидкой: тратой они не были. */
  free: { name: string; qty: number | null }[];
  problems: string[];
  merchant: string | null;
  date: PlainDate | null;
  time: string | null;
  /** Операции снимка экрана банка, выписки (`statement`) и квитанции (`bank_slip`). */
  operations: StatementOp[];
  /** Сколько строк списка не прочиталось: человек должен знать, что видит не всё. */
  droppedOperations: number;
}

/**
 * Операция со снимка экрана банка.
 *
 * Сумма проверена сервером по распознанному тексту: она стоит на экране.
 * Дата есть, только если над операцией виден заголовок дня.
 */
export interface StatementOp {
  title: string;
  /** Копейки целым числом, без знака. */
  amount: number;
  direction: 'out' | 'in';
  type: 'purchase' | 'transfer' | 'income' | 'other';
  bankCategory: string | null;
  /** Подпись карты или счёта в банке: «Black», «Credit card». */
  account: string | null;
  date: PlainDate | null;
  time: string | null;
  /** Чек возврата: строка сразу помечена «Это возврат за покупку». */
  refund?: true;
}

/**
 * Разбор электронного документа: страницы уже нарисованы клиентом.
 *
 * Отдельный маршрут, а не флаг у разбора снимка: у него своя проверка доступа
 * (Pro), свой пакет единиц и свой ключ учёта — хеш файла, по которому повтор
 * не списывает вторую единицу.
 */
export async function parseDocument(doc: ({
  pages: { base64: string; mimeType: string }[];
} | {
  /** Выписка с текстовым слоем: строки по страницам, без картинок. */
  text: string[][];
}) & {
  fileHash: string;
  /** День человека: «Сегодня» и «Вчера» на снимке банка считаются от него. */
  today: PlainDate;
}): Promise<ParsedReceipt> {
  const body = await unwrap(await api('/ai/parse-document', {
    method: 'POST',
    body: JSON.stringify({
      ...('text' in doc
        ? { text: doc.text }
        : { pages: doc.pages.map((p) => ({ base64: p.base64, mimeType: p.mimeType })) }),
      fileHash: doc.fileHash,
      today: doc.today,
    }),
  }));
  return normalizeReceipt((body as { result?: unknown }).result);
}

/**
 * Части плана для позиций чека — по названиям, моделью.
 *
 * Словарь ядра отвечает мгновенно и всегда, но видит только записанные в нём
 * слова; в чеке названия сокращены до «Нап.тон.газ.» и «Леп.ТОРТИЛ.пшен.», и
 * правил на них не напасёшься. Модель получает **только названия** — ни цен,
 * ни сумм, — и отвечает закрытым списком из двух слов.
 *
 * `null` означает «модель не помогла»: нет сети, нет доступа, ответ не той
 * формы. Это не ошибка экрана — наверху остаётся разбор словаря, и человек
 * правит его тапом, как правил всегда.
 */
export async function classifyBasket(names: readonly string[]): Promise<PlanPart[] | null> {
  if (names.length === 0) return null;
  try {
    const body = await unwrap(await api('/ai/classify-basket', {
      method: 'POST',
      body: JSON.stringify({ items: names }),
    }));
    const parts = (body as { parts?: unknown }).parts;
    if (!Array.isArray(parts) || parts.length !== names.length) return null;
    // Проверяем и здесь: сервер и клиент отвечают за свой конец одинаково, а
    // разъехавшаяся длина списка тихо сдвинула бы разметку на соседний товар.
    return parts.every((p) => p === 'need' || p === 'want') ? parts as PlanPart[] : null;
  } catch {
    return null;
  }
}

/**
 * Категория каждой позиции чека — из справочника человека.
 *
 * `null` в ответе — модель не узнала товар: такую строку человек увидит
 * отмеченной. `null` вместо ответа — сбой, и экран остаётся рабочим: категорию
 * выберут руками.
 */
export async function categorizeBasket(
  names: readonly string[], categories: readonly { id: string; name: string }[],
): Promise<(string | null)[] | null> {
  if (names.length === 0 || categories.length === 0) return null;
  return inChunks(names, (chunk) => categorizeChunk(chunk, categories));
}

/** Сколько строк сервер принимает в одном запросе категорий (`MAX_ITEMS` в basket.mjs). */
const BASKET_CHUNK = 60;

/**
 * Список названий — частями, которые принимает сервер.
 *
 * В выписке за месяц полторы сотни строк, а сервер берёт за раз 60: целиком
 * такой список возвращался урезанным, отбрасывался, и ни одна строка не
 * получала категорию. Не ответившая часть — строки с `null`: их человек
 * выберет сам; `null` вместо ответа — не ответила ни одна часть.
 *
 * Одинаковое название уходит один раз (15.10.2026): в выписке «Оплата в
 * MAGNIT» встречается двадцать раз, и разных магазинов за месяц обычно меньше
 * шестидесяти — список укладывается в один вызов. Тогда похожие покупки
 * получают одну и ту же категорию, а не «Спорт» в одной части и «Фитнес» в
 * другой.
 */
async function inChunks<T>(
  names: readonly string[], call: (chunk: readonly string[]) => Promise<(T | null)[] | null>,
): Promise<(T | null)[] | null> {
  const key = (n: string) => n.trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru');
  const slot = new Map<string, number>();
  const unique: string[] = [];
  const at = names.map((n) => {
    const k = key(n);
    if (!slot.has(k)) { slot.set(k, unique.length); unique.push(n); }
    return slot.get(k)!;
  });
  const chunks: (readonly string[])[] = [];
  for (let i = 0; i < unique.length; i += BASKET_CHUNK) chunks.push(unique.slice(i, i + BASKET_CHUNK));
  const parts = await Promise.all(chunks.map(call));
  if (parts.every((p) => p === null)) return null;
  const answers = parts.flatMap((p, k) => p ?? chunks[k]!.map(() => null));
  return at.map((i) => answers[i] ?? null);
}

async function categorizeChunk(
  names: readonly string[], categories: readonly { id: string; name: string }[],
): Promise<(string | null)[] | null> {
  try {
    const body = await unwrap(await api('/ai/categorize-basket', {
      method: 'POST',
      body: JSON.stringify({ items: names, categories }),
    }));
    const list = (body as { categories?: unknown }).categories;
    if (!Array.isArray(list) || list.length !== names.length) return null;
    const ids = new Set(categories.map((c) => c.id));
    return list.every((x) => x === null || (typeof x === 'string' && ids.has(x)))
      ? list as (string | null)[]
      : null;
  } catch {
    return null;
  }
}

/** Новая категория, предложенная моделью. Создаёт её приложение, при «Записать». */
export interface CategorySuggestion {
  name: string;
  /** Категория человека, внутрь которой новая входит; `null` — верхний уровень. */
  parentId: string | null;
  part: PlanPart;
  icon: string;
}

/**
 * Недостающие категории — для покупок, которым не нашлось места в
 * справочнике. Как у категорий позиций: в вызов уходят только названия.
 *
 * `null` в ответе — модель не придумала категорию; `null` вместо ответа —
 * сбой, и покупка остаётся там, куда её положили.
 */
export async function suggestCategories(
  names: readonly string[], categories: readonly { id: string; name: string }[], icons: readonly string[],
): Promise<(CategorySuggestion | null)[] | null> {
  if (names.length === 0 || categories.length === 0 || icons.length === 0) return null;
  return inChunks(names, (chunk) => suggestChunk(chunk, categories, icons));
}

async function suggestChunk(
  names: readonly string[], categories: readonly { id: string; name: string }[], icons: readonly string[],
): Promise<(CategorySuggestion | null)[] | null> {
  try {
    const body = await unwrap(await api('/ai/suggest-categories', {
      method: 'POST',
      body: JSON.stringify({ items: names, categories, icons }),
    }));
    const list = (body as { suggestions?: unknown }).suggestions;
    if (!Array.isArray(list) || list.length !== names.length) return null;
    const ids = new Set(categories.map((c) => c.id));
    return list.map((x): CategorySuggestion | null => {
      const s = (typeof x === 'object' && x !== null ? x : {}) as Record<string, unknown>;
      if (typeof s.name !== 'string' || !s.name.trim() || (s.part !== 'need' && s.part !== 'want')) return null;
      return {
        name: s.name.trim(),
        parentId: typeof s.parentId === 'string' && ids.has(s.parentId) ? s.parentId : null,
        part: s.part,
        icon: typeof s.icon === 'string' ? s.icon : 'shapes',
      };
    });
  } catch {
    return null;
  }
}

export async function parseReceiptPhoto(image: string, mimeType: string): Promise<ParsedReceipt> {
  const body = await unwrap(await api('/ai/parse-receipt', {
    method: 'POST',
    body: JSON.stringify({ image, mimeType }),
  }));
  return normalizeReceipt((body as { result?: unknown }).result);
}

/**
 * Приведение разбора чека к типу.
 *
 * Признак доверия здесь не пересчитывается — его выносит сервер, где лежат обе
 * арифметические проверки. Но состав при недоверии обнуляется и тут: показать
 * половину чека хуже, чем не показать ничего, потому что человек решит, что
 * видит весь чек.
 */
const KINDS: ImportedReceipt['kind'][] = ['sale', 'refund', 'order', 'bank_slip', 'statement'];

function normalizeReceipt(raw: unknown): ParsedReceipt {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const trusted = r.trusted === true;
  // Незнакомый тип документа — «unknown», а не «покупка»: о документе, который
  // мы не опознали, деньги решать не должны.
  const kind = KINDS.find((k) => k === r.kind) ?? 'unknown';
  const items = trusted && Array.isArray(r.items) ? r.items.flatMap(toItem) : [];
  let date: PlainDate | null = null;
  if (typeof r.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.date)) {
    try { date = pd(r.date); } catch { date = null; }
  }
  return {
    trusted: trusted && items.length > 0,
    kind,
    totalFromItems: r.totalFromItems === true,
    total: typeof r.total === 'number' && Number.isInteger(r.total) && r.total > 0 ? r.total : null,
    items,
    free: Array.isArray(r.free)
      ? r.free.flatMap((f) => {
          const o = (typeof f === 'object' && f !== null ? f : {}) as Record<string, unknown>;
          return typeof o.name === 'string' && o.name
            ? [{ name: o.name, qty: typeof o.qty === 'number' ? o.qty : null }]
            : [];
        })
      : [],
    account: text(r.account, 80),
    problems: Array.isArray(r.problems) ? r.problems.filter((p): p is string => typeof p === 'string') : [],
    merchant: typeof r.merchant === 'string' && r.merchant !== '' ? r.merchant : null,
    date,
    time: typeof r.time === 'string' && /^\d{2}:\d{2}$/.test(r.time) ? r.time : null,
    // Операции есть у снимка экрана банка, у выписки и у квитанции об одной операции.
    operations: (kind === 'statement' || kind === 'bank_slip') && Array.isArray(r.operations) ? r.operations.flatMap(toOp) : [],
    droppedOperations: typeof r.droppedOperations === 'number' && r.droppedOperations > 0 ? r.droppedOperations : 0,
  };
}

const OP_TYPES: StatementOp['type'][] = ['purchase', 'transfer', 'income', 'other'];
const text = (v: unknown, n: number): string | null => (typeof v === 'string' && v.trim() ? v.trim().slice(0, n) : null);

function toOp(raw: unknown): StatementOp[] {
  const o = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const title = text(o.title, 80);
  if (!title) return [];
  if (typeof o.amount !== 'number' || !Number.isInteger(o.amount) || o.amount <= 0) return [];
  let date: PlainDate | null = null;
  if (typeof o.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.date)) {
    try { date = pd(o.date); } catch { date = null; }
  }
  return [{
    title,
    amount: o.amount,
    direction: o.direction === 'in' ? 'in' : 'out',
    type: OP_TYPES.find((t) => t === o.type) ?? (o.direction === 'in' ? 'income' : 'purchase'),
    bankCategory: text(o.bankCategory, 40),
    account: text(o.account, 40),
    date,
    time: typeof o.time === 'string' && /^\d{2}:\d{2}$/.test(o.time) ? o.time : null,
  }];
}

function toItem(raw: unknown): ReceiptItem[] {
  const o = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  if (typeof o.name !== 'string' || o.name === '') return [];
  if (typeof o.qty !== 'number' || !(o.qty > 0)) return [];
  if (typeof o.price !== 'number' || !Number.isInteger(o.price) || o.price < 0) return [];
  if (typeof o.sum !== 'number' || !Number.isInteger(o.sum) || o.sum <= 0) return [];
  return [{ name: o.name.slice(0, 200), qty: o.qty, price: o.price, sum: o.sum }];
}
